// Small particle helpers (smoke, fire, sparks, rain) built from canvas textures — no asset files.
import { useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";

let radialTex: THREE.Texture | null = null;
/** A soft round blob, shared by every particle system. */
export function radialTexture(): THREE.Texture {
  if (radialTex) return radialTex;
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d")!;
  const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grd.addColorStop(0, "rgba(255,255,255,1)");
  grd.addColorStop(0.4, "rgba(255,255,255,0.55)");
  grd.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grd;
  g.fillRect(0, 0, 64, 64);
  radialTex = new THREE.CanvasTexture(c);
  return radialTex;
}

function rand(seed: number): () => number {
  let t = seed >>> 0;
  return () => { t = (t + 0x6d2b79f5) | 0; let x = Math.imul(t ^ (t >>> 15), t | 1); x ^= x + Math.imul(x ^ (x >>> 7), x | 61); return ((x ^ (x >>> 14)) >>> 0) / 4294967296; };
}

/** Rising puffs (chimney smoke, burning rooms). */
export function Plume({ position, count = 18, color = "#5a5550", spread = 0.35, height = 4, size = 1.1, speed = 0.5, additive = false, opacity = 0.55, seed = 1 }: {
  position: [number, number, number]; count?: number; color?: string; spread?: number; height?: number; size?: number;
  speed?: number; additive?: boolean; opacity?: number; seed?: number;
}) {
  const r = useMemo(() => rand(seed), [seed]);
  const parts = useMemo(() => Array.from({ length: count }, () => ({ t: r(), dx: (r() - 0.5) * spread, dz: (r() - 0.5) * spread, drift: (r() - 0.3) * 0.8 })), [count, spread, r]);
  const refs = useRef<(THREE.Sprite | null)[]>([]);
  useFrame((_, dt) => {
    parts.forEach((p, i) => {
      p.t = (p.t + dt * speed / height * 2) % 1;
      const s = refs.current[i];
      if (!s) return;
      s.position.set(p.dx + p.drift * p.t * 1.5, p.t * height, p.dz + p.t * 0.6);
      const k = size * (0.4 + p.t * 1.4);
      s.scale.set(k, k, k);
      (s.material as THREE.SpriteMaterial).opacity = opacity * Math.sin(Math.PI * Math.min(1, p.t * 1.2));
    });
  });
  const tex = radialTexture();
  return (
    <group position={position}>
      {parts.map((_, i) => (
        <sprite key={i} ref={el => { refs.current[i] = el; }}>
          <spriteMaterial map={tex} color={color} transparent depthWrite={false} blending={additive ? THREE.AdditiveBlending : THREE.NormalBlending} />
        </sprite>
      ))}
    </group>
  );
}

/** Flickering flames + smoke for a damaged room. */
export function Fire({ position, seed = 1 }: { position: [number, number, number]; seed?: number }) {
  const light = useRef<THREE.PointLight>(null);
  useFrame(({ clock }) => {
    if (light.current) light.current.intensity = 6 + Math.sin(clock.elapsedTime * 13 + seed) * 2 + Math.sin(clock.elapsedTime * 7.3) * 1.5;
  });
  return (
    <group position={position}>
      <pointLight ref={light} color="#ff7a2a" distance={5} decay={2} position={[0, 0.6, 0]} />
      <Plume position={[0, 0, 0]} count={14} color="#ff8a2a" spread={0.9} height={1.4} size={0.7} speed={1.4} additive opacity={0.9} seed={seed} />
      <Plume position={[0, 0.8, 0]} count={14} color="#2b2522" spread={0.7} height={4} size={1.3} speed={0.6} opacity={0.6} seed={seed + 7} />
    </group>
  );
}

/** Falling rain around the camera target; `amount` 0..1. */
export function Rain({ amount }: { amount: number }) {
  const n = 1400;
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(n * 6);
    const r = rand(42);
    for (let i = 0; i < n; i++) {
      const x = (r() - 0.5) * 70, y = r() * 30, z = (r() - 0.5) * 50;
      pos.set([x, y, z, x - 0.12, y - 0.9, z + 0.05], i * 6);
    }
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, []);
  const ref = useRef<THREE.LineSegments>(null);
  useFrame((_, dt) => {
    const l = ref.current;
    if (!l) return;
    const a = l.geometry.getAttribute("position") as THREE.BufferAttribute;
    const arr = a.array as Float32Array;
    for (let i = 0; i < n; i++) {
      const o = i * 6;
      let y = arr[o + 1] - dt * 26;
      if (y < -1) y += 31;
      arr[o + 1] = y; arr[o + 4] = y - 0.9;
    }
    a.needsUpdate = true;
    (l.material as THREE.LineBasicMaterial).opacity = amount * 0.45;
    l.visible = amount > 0.02;
  });
  return (
    <lineSegments ref={ref} geometry={geo} frustumCulled={false}>
      <lineBasicMaterial color="#b9c8d6" transparent opacity={0} depthWrite={false} />
    </lineSegments>
  );
}

/** A one-shot burst (explosion, splash) that fades out over `life` seconds. */
export function Burst({ position, color, count = 26, life = 1.4, size = 0.9, spread = 3, up = 3, seed = 3, additive = true }: {
  position: [number, number, number]; color: string; count?: number; life?: number; size?: number; spread?: number; up?: number; seed?: number;
  /** Additive glow (fire, sparks) or normal blending (smoke, oil, water). */
  additive?: boolean;
}) {
  const r = useMemo(() => rand(seed), [seed]);
  const parts = useMemo(() => Array.from({ length: count }, () => {
    const a = r() * Math.PI * 2, s = 0.3 + r() * 0.7;
    return { vx: Math.cos(a) * s * spread, vz: Math.sin(a) * s * spread, vy: (0.4 + r()) * up };
  }), [count, spread, up, r]);
  const t = useRef(0);
  const refs = useRef<(THREE.Sprite | null)[]>([]);
  useFrame((_, dt) => {
    t.current += dt;
    const k = t.current / life;
    parts.forEach((p, i) => {
      const s = refs.current[i];
      if (!s) return;
      const tt = Math.min(t.current, life);
      s.position.set(p.vx * tt, p.vy * tt - 4.9 * tt * tt * 0.5, p.vz * tt);
      const sc = size * (0.6 + k);
      s.scale.set(sc, sc, sc);
      (s.material as THREE.SpriteMaterial).opacity = Math.max(0, 1 - k);
    });
  });
  const tex = radialTexture();
  return (
    <group position={position}>
      {parts.map((_, i) => (
        <sprite key={i} ref={el => { refs.current[i] = el; }}>
          <spriteMaterial map={tex} color={color} transparent depthWrite={false} blending={additive ? THREE.AdditiveBlending : THREE.NormalBlending} />
        </sprite>
      ))}
    </group>
  );
}
